import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ChartDialogConfig } from "../../../../../common/constants";

const MAX_Y_AXIS_VALUE = ChartDialogConfig.MAX_Y_AXIS_VALUE;
const MIN_Y_AXIS_VALUE = ChartDialogConfig.MIN_Y_AXIS_VALUE;

export interface ChartEditIdParam {
  /**
   * ID項目一覧
   */
  items: { text: string; value: string }[];
  /**
   * IDが変更された場合に呼び出されます
   * @param id - ID
   */
  onChange?: (id: string) => void;
}

export interface ChartEditXParam {
  /**
   * X軸開始位置
   */
  start: number | undefined;
  /**
   * X軸終了位置
   */
  end: number | undefined;
  /**
   * X軸位置が変更された場合に呼び出されます。
   * @param start - X軸開始位置
   * @param end - X軸終了位置
   */
  onChange: (start: number | undefined, end: number | undefined) => void;
}

export interface ChartEditRangeParam {
  /**
   * X軸範囲
   */
  x: { min: number; max: number };
}

export interface ChartEditMenuParam {
  /**
   * ID
   */
  id?: ChartEditIdParam;
  /**
   * X位置
   */
  x?: ChartEditXParam;
  /**
   * 軸範囲
   */
  range?: ChartEditRangeParam;
  /**
   * 登録ボタンが押された場合に呼び出されます
   * @param id - id
   * @param start - X軸開始位置
   * @param end - X軸終了位置
   * @param value - 設定値
   */
  onRegist?: (
    id: string,
    start: number,
    end: number | undefined,
    value: number
  ) => void;
}

function isValueEnabled(value: number) {
  return !isNaN(value) && MIN_Y_AXIS_VALUE <= value && value <= MAX_Y_AXIS_VALUE;
}

function isRegisButtonEnabled(
  start: string,
  end: string,
  value: string,
  range?: ChartEditRangeParam
) {
  if (value.length > 0 && start.length > 0) {
    const numValue = Number(value);
    const numStart = Number(start);
    if (
      isValueEnabled(numValue) &&
      !isNaN(numStart) &&
      Number.isInteger(numStart) &&
      range &&
      range.x.min <= numStart &&
      numStart <= range.x.max
    ) {
      if (end.length === 0) {
        return true;
      } else {
        const numEnd = Number(end);
        if (
          !isNaN(numEnd) &&
          Number.isInteger(numEnd) &&
          numStart <= numEnd &&
          range.x.min <= numEnd &&
          numEnd <= range.x.max
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * チャートEditメニューを表示します
 * @param {ChartEditMenuParam} props - Editメニューパラメータ
 * @returns チャートEditメニュー
 */
export default function ChartEditMenu(props: ChartEditMenuParam) {
  const { id, x, range, onRegist } = props;
  const { t } = useTranslation();
  const [select, setSelect] = useState<string>("");
  const [start, setStart] = useState<string>("");
  const [end, setEnd] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [regisButtonEnabled, setRegisButtonEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (id && id.items.length > 0 && select.length === 0) {
      const value = id.items[0].value;
      setSelect(value);
      if (id.onChange) {
        id.onChange(value);
      }
    }
    if (x) {
      const startX = typeof x.start === "undefined" ? "" : x.start.toString();
      if (startX !== start) {
        setStart(startX);
      }
      const endX = typeof x.end === "undefined" ? "" : x.end.toString();
      if (endX !== end) {
        setEnd(endX);
      }
      setRegisButtonEnabled(isRegisButtonEnabled(startX, endX, value, range));
    }
  }, [props]);

  function handleChangeLegend(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    setSelect(value);
    if (id && id.onChange) {
      id.onChange(value);
    }
  }

  function handleChangeStart(e: React.ChangeEvent<HTMLInputElement>) {
    const targetValue = e.target.value;
    const endX = end.length === 0 ? undefined : Number(end);
    let enabled = false;
    if (targetValue.length === 0) {
      setStart("");
      if (x) {
        x.onChange(undefined, endX);
      }
      enabled = isRegisButtonEnabled(targetValue, end, value, range);
    } else {
      const numValue = Number(targetValue);
      if (!isNaN(numValue) && Number.isInteger(numValue)) {
        let trunc = Math.trunc(numValue);
        if (range) {
          if (trunc < range.x.min) {
            trunc = range.x.min;
          }
          if (trunc > range.x.max) {
            trunc = range.x.max;
          }
        }
        setStart(trunc.toString());
        if (x) {
          x.onChange(trunc, endX);
        }
        enabled = isRegisButtonEnabled(trunc.toString(), end, value, range);
      }
    }
    setRegisButtonEnabled(enabled);
  }

  function handleChangeEnd(e: React.ChangeEvent<HTMLInputElement>) {
    const targetValue = e.target.value;
    const startX = start.length === 0 ? undefined : Number(start);
    let enabled = false;
    if (targetValue.length === 0) {
      setEnd("");
      if (x) {
        x.onChange(startX, undefined);
      }
      enabled = isRegisButtonEnabled(start, targetValue, value, range);
    } else {
      const numValue = Number(targetValue);
      if (!isNaN(numValue) && Number.isInteger(numValue)) {
        let trunc = Math.trunc(numValue);
        if (range) {
          if (trunc < range.x.min) {
            trunc = range.x.min;
          }
          if (trunc > range.x.max) {
            trunc = range.x.max;
          }
        }
        setEnd(trunc.toString());
        if (x) {
          x.onChange(startX, trunc);
        }
        enabled = isRegisButtonEnabled(start, trunc.toString(), value, range);
      }
    }
    setRegisButtonEnabled(enabled);
  }

  function handleChangeValue(e: React.ChangeEvent<HTMLInputElement>) {
    const targetValue = e.target.value;
    let enabled = false;
    if (targetValue.length === 0) {
      setValue("");
    } else if (targetValue === "-") {
      setValue("-");
    } else {
      const numValue = Number(targetValue);
      if (isValueEnabled(numValue)) {
        setValue(targetValue);
        enabled = isRegisButtonEnabled(start, end, targetValue, range);
      }
    }
    setRegisButtonEnabled(enabled);
  }

  function handleRegist() {
    if (onRegist && value.length > 0 && start.length > 0) {
      onRegist(
        select,
        Number(start),
        end.length === 0 ? undefined : Number(end),
        Number(value)
      );
    }
  }

  const selectOptions = id
    ? id.items.map((item, i) => {
        return (
          <option value={item.value} key={i}>
            {item.text}
          </option>
        );
      })
    : "";
  const selectForm =
    selectOptions.length > 0 && select.length > 0 ? (
      <Form.Select value={select} onChange={handleChangeLegend}>
        {selectOptions}
      </Form.Select>
    ) : (
      ""
    );

  const regisButton = regisButtonEnabled ? (
    <Button variant="primary" onClick={handleRegist}>
      {t("chart_edit_register")}
    </Button>
  ) : (
    ""
  );

  return (
    <Form style={{ width: "120px" }}>
      {selectForm}
      <div style={{ height: "25px" }}></div>
      <div>
        <div>{t("chart_edit_start")}</div>
        <Form.Control
          style={{ textAlign: "right" }}
          value={start}
          onChange={handleChangeStart}
        ></Form.Control>
      </div>
      <div style={{ height: "25px" }}></div>
      <div>
        <div>{t("chart_edit_end")}</div>
        <Form.Control
          style={{ textAlign: "right" }}
          value={end}
          onChange={handleChangeEnd}
        ></Form.Control>
      </div>
      <div style={{ height: "25px" }}></div>
      <div>
        <div>{t("chart_edit_value")}</div>
        <Form.Control
          style={{ textAlign: "right" }}
          value={value}
          onChange={handleChangeValue}
        ></Form.Control>
      </div>
      <div style={{ height: "25px" }}></div>
      <div>{regisButton}</div>
    </Form>
  );
}
